import { useEffect, useRef, useState } from "react";
import {
    Phone,
    Mail,
    ShieldCheck,
    MapPin,
    Clock,
    X,
    ZoomIn,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import WhatsAppIcon from "./icons/WhatsAppIcon";
import { BUSINESS } from "../lib/business";

export default function Hero() {
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const touchStartX = useRef(null);

    const beforeAfter = [
        {
            src: "/assets/kitchen_before.jpg",
            label: "Before",
            sub: "Original kitchen",
        },
        {
            src: "/assets/kitchen_after.jpg",
            label: "After",
            sub: "Finished by Call The Handyman",
        },
    ];

    const current =
        lightboxIndex !== null ? beforeAfter[lightboxIndex] : null;
    const showPrev = () =>
        setLightboxIndex((i) =>
            i === null ? null : (i - 1 + beforeAfter.length) % beforeAfter.length,
        );
    const showNext = () =>
        setLightboxIndex((i) =>
            i === null ? null : (i + 1) % beforeAfter.length,
        );

    useEffect(() => {
        if (lightboxIndex === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") setLightboxIndex(null);
            else if (e.key === "ArrowLeft") showPrev();
            else if (e.key === "ArrowRight") showNext();
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lightboxIndex]);

    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(dx) < 40) return;
        if (dx < 0) showNext();
        else showPrev();
    };

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative overflow-hidden bg-white"
        >
            <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-amber-100/40 blur-3xl pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 md:pt-24 md:pb-32 lg:pt-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    <div
                        className="lg:col-span-7 reveal"
                        data-testid="hero-content"
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 text-xs font-semibold uppercase tracking-[0.18em] text-slate-700 mb-6">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            <span data-testid="hero-availability-badge">
                                Available this week
                            </span>
                        </div>

                        <h1
                            className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-slate-900 leading-[1.02]"
                            data-testid="hero-headline"
                        >
                            Reliable handyman
                            <br />
                            services you can{" "}
                            <span className="relative inline-block">
                                <span className="relative z-10">trust.</span>
                                <span className="absolute bottom-2 left-0 right-0 h-3 bg-amber-400/60 z-0" />
                            </span>
                        </h1>

                        <p
                            className="mt-7 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl"
                            data-testid="hero-subheading"
                        >
                            From plumbing and electrics to flat-pack assembly
                            and TV mounting — one trusted local tradesman for
                            all those jobs on your list. Professional, tidy,
                            and on time.
                        </p>

                        <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3 sm:items-center">
                            <a
                                href={`tel:${BUSINESS.phoneTel}`}
                                data-testid="hero-call-cta"
                                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md bg-amber-600 hover:bg-amber-700 text-white font-semibold shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5"
                            >
                                <Phone className="w-5 h-5" />
                                <span>Call {BUSINESS.phone}</span>
                            </a>
                            <a
                                href={BUSINESS.whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-whatsapp-cta"
                                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold shadow-md transition-all hover:-translate-y-0.5"
                            >
                                <WhatsAppIcon className="w-5 h-5" />
                                <span>WhatsApp</span>
                            </a>
                            <a
                                href={`mailto:${BUSINESS.email}`}
                                data-testid="hero-email-cta"
                                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-semibold transition-all"
                            >
                                <Mail className="w-5 h-5" />
                                <span>Email us</span>
                            </a>
                        </div>

                        <div
                            className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-xl"
                            data-testid="hero-trust-strip"
                        >
                            <div className="flex items-start gap-3">
                                <ShieldCheck className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                                <div>
                                    <div className="text-sm font-bold text-slate-900">
                                        Trusted local
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        Years of experience
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                                <div>
                                    <div className="text-sm font-bold text-slate-900">
                                        Dumbarton based
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        & surrounding areas
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                                <div>
                                    <div className="text-sm font-bold text-slate-900">
                                        Free quotes
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        No callout fee
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        className="lg:col-span-5 relative reveal"
                        style={{ animationDelay: "0.15s" }}
                        data-testid="hero-image-wrap"
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-900 shadow-sm mb-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Real job · Before & after
                        </div>
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            {beforeAfter.map((img, idx) => (
                                <button
                                    key={img.label}
                                    type="button"
                                    onClick={() => setLightboxIndex(idx)}
                                    data-testid={`hero-${img.label.toLowerCase()}-image`}
                                    className="group relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl aspect-[3/4] bg-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-all hover:shadow-2xl hover:-translate-y-0.5"
                                    aria-label={`Enlarge ${img.label.toLowerCase()} photo`}
                                >
                                    <img
                                        src={img.src}
                                        alt={`${img.label} — kitchen refit`}
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 via-slate-900/10 to-transparent" />
                                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-white text-slate-900 text-[10px] font-extrabold uppercase tracking-[0.18em] shadow">
                                        {img.label}
                                    </div>
                                    <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow">
                                        <ZoomIn className="w-4 h-4 text-slate-900" />
                                    </div>
                                    <div className="absolute left-3 right-3 bottom-3 text-white">
                                        <div className="text-xs font-semibold opacity-90">
                                            {img.sub}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>

                        <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                                <ShieldCheck className="w-5 h-5 text-amber-700" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-display font-bold text-slate-900 text-sm">
                                    One call. Job done.
                                </div>
                                <div className="text-xs text-slate-600">
                                    Honest pricing · Tidy workmanship · Tap a photo to enlarge
                                </div>
                            </div>
                            <div className="hidden sm:flex flex-col items-end pl-3 border-l border-slate-200">
                                <div className="text-[9px] uppercase tracking-[0.2em] text-amber-700 font-bold">
                                    Reply within
                                </div>
                                <div className="font-display text-base font-extrabold text-slate-900 leading-none mt-0.5">
                                    2 hours
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {current && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${current.label} photo enlarged`}
                    data-testid="hero-lightbox"
                    className="fixed inset-0 z-[100] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in"
                    onClick={() => setLightboxIndex(null)}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setLightboxIndex(null);
                        }}
                        data-testid="hero-lightbox-close"
                        aria-label="Close enlarged photo"
                        className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center transition-colors z-10"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            showPrev();
                        }}
                        data-testid="hero-lightbox-prev"
                        aria-label="Previous photo"
                        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center transition-colors z-10"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            showNext();
                        }}
                        data-testid="hero-lightbox-next"
                        aria-label="Next photo"
                        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center transition-colors z-10"
                    >
                        <ChevronRight className="w-6 h-6" />
                    </button>

                    <figure
                        key={lightboxIndex}
                        className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center reveal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={current.src}
                            alt={`${current.label} — kitchen refit`}
                            className="max-w-full max-h-[78vh] w-auto h-auto rounded-xl shadow-2xl object-contain select-none"
                            draggable={false}
                        />
                        <figcaption className="mt-4 flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm text-white text-sm">
                            <span className="font-bold uppercase tracking-[0.18em] text-amber-400">
                                {current.label}
                            </span>
                            <span className="opacity-90">{current.sub}</span>
                            <span className="opacity-60 text-xs tabular-nums pl-2 border-l border-white/20">
                                {lightboxIndex + 1} / {beforeAfter.length}
                            </span>
                        </figcaption>
                        <div
                            data-testid="hero-lightbox-hint"
                            className="mt-3 text-[11px] uppercase tracking-[0.2em] text-white/50"
                        >
                            Swipe or use ← → to compare
                        </div>
                    </figure>
                </div>
            )}
        </section>
    );
}
